import React, { useEffect, useState, useRef, createElement } from 'react';
import { MailIcon, PhoneIcon, CheckCircleIcon, AlertCircleIcon, LoaderIcon } from 'lucide-react';
import emailjs from '@emailjs/browser';
declare global {
  interface Window {
    grecaptcha: any;
    onRecaptchaLoad: () => void;
  }
}
export const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: '',
    honeypot: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    email?: string;
    message?: string;
    recaptcha?: string;
  }>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const sectionRef = useRef(null);
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const onRecaptchaSuccess = (token: string) => {
    console.log('reCAPTCHA validated successfully:', token.substring(0, 10) + '...');
    setRecaptchaToken(token);
    setFormErrors(prev => ({
      ...prev,
      recaptcha: undefined
    }));
  };
  const resetRecaptcha = () => {
    if (window.grecaptcha && recaptchaLoaded) {
      try {
        window.grecaptcha.reset();
        setRecaptchaToken(null);
      } catch (error) {
        console.error('Error resetting reCAPTCHA:', error);
      }
    }
  };
  useEffect(() => {
    window.onRecaptchaLoad = () => {
      if (recaptchaRef.current) {
        try {
          window.grecaptcha.render(recaptchaRef.current, {
            sitekey: '6LfHih4rAAAAAP_yB0EU_qKzkDYyfOrMQ78OsFMr',
            callback: onRecaptchaSuccess,
            theme: 'dark'
          });
          setRecaptchaLoaded(true);
          console.log('reCAPTCHA initialized successfully');
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
        }
      }
    };
    if (!document.querySelector('script[src*="recaptcha/api.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      return () => {
        document.head.removeChild(script);
      };
    } else if (window.grecaptcha && window.grecaptcha.render) {
      window.onRecaptchaLoad();
    }
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.disconnect();
      }
    };
  }, []);
  useEffect(() => {
    emailjs.init('Q1mKAAc2_gSL30mNp');
  }, []);
  const validateForm = () => {
    const errors: {
      name?: string;
      email?: string;
      message?: string;
      recaptcha?: string;
    } = {};
    if (!formState.name.trim()) {
      errors.name = 'Naam is verplicht';
    }
    if (!formState.email.trim()) {
      errors.email = 'Email is verplicht';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      errors.email = 'Voer een geldig emailadres in';
    }
    if (!formState.message.trim()) {
      errors.message = 'Bericht is verplicht';
    }
    if (!recaptchaToken) {
      errors.recaptcha = 'Vul de reCAPTCHA in om te bevestigen dat u geen robot bent';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const isSpam = () => {
    if (formState.honeypot) {
      console.log('Honeypot detected');
      return true;
    }
    const now = Date.now();
    if (now - lastSubmissionTime < 5 * 60 * 1000) {
      if (submissionCount >= 3) {
        console.log('Rate limit exceeded');
        return true;
      }
    } else {
      setSubmissionCount(0);
    }
    const spamKeywords = ['viagra', 'cialis', 'casino', 'lottery', 'prize', 'winner', 'free money'];
    const lowerCaseMessage = formState.message.toLowerCase();
    if (spamKeywords.some(keyword => lowerCaseMessage.includes(keyword))) {
      console.log('Spam keywords detected');
      return true;
    }
    const linkCount = (formState.message.match(/https?:\/\//g) || []).length;
    if (linkCount > 3) {
      console.log('Too many links detected');
      return true;
    }
    return false;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (isSpam()) {
      setFormStatus('loading');
      setTimeout(() => {
        setFormStatus('success');
        setFormState({
          name: '',
          email: '',
          message: '',
          honeypot: ''
        });
        resetRecaptcha();
      }, 1500);
      return;
    }
    setSubmissionCount(prev => prev + 1);
    setLastSubmissionTime(Date.now());
    setFormStatus('loading');
    try {
      const currentDate = new Date().toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      const templateParams = {
        name: formState.name,
        email: formState.email,
        message: formState.message,
        date: currentDate,
        recaptcha: recaptchaToken,
        subject: `Nieuw bericht via contactformulier van ${formState.name} op ${currentDate}`
      };
      console.log('Sending email with params:', {
        ...templateParams,
        recaptcha: templateParams.recaptcha ? 'token-present' : 'missing'
      });
      const result = await emailjs.send('service_9zxsdbe', 'template_y2yvfzk', templateParams);
      console.log('Email successfully sent!', result);
      setFormStatus('success');
      setFormState({
        name: '',
        email: '',
        message: '',
        honeypot: ''
      });
      resetRecaptcha();
      setTimeout(() => {
        setFormStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Error sending email:', error);
      setFormStatus('error');
      resetRecaptcha();
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Er is iets misgegaan bij het verzenden. Probeer het later opnieuw.');
      }
      setTimeout(() => {
        setFormStatus('idle');
        setErrorMessage('');
      }, 5000);
    }
  };
  return <section id="contact" ref={sectionRef} className="py-24 bg-black w-full" data-id="element-82">
      <div className="container mx-auto px-4 md:px-6" data-id="element-83">
        <h2 className={`text-3xl md:text-4xl font-bold mb-12 text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-84">
          Contact
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-85">
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-86">
            <h3 className="text-2xl font-semibold mb-6" data-id="element-87">
              Neem contact met ons op
            </h3>
            <p className="text-gray-300 mb-8" data-id="element-88">
              Heeft u interesse in een samenwerking of wilt u meer informatie
              over onze diensten? Neem gerust contact met ons op.
            </p>
            <div className="space-y-6" data-id="element-89">
              <div className="flex items-start" data-id="element-90">
                <PhoneIcon className="text-blue-400 mt-1 mr-4" size={20} data-id="element-91" />
                <div data-id="element-92">
                  <h4 className="font-medium mb-1" data-id="element-93">Telefoon</h4>
                  <p className="text-gray-400" data-id="element-94">+31651764541</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-95">
                <MailIcon className="text-blue-400 mt-1 mr-4" size={20} data-id="element-96" />
                <div data-id="element-97">
                  <h4 className="font-medium mb-1" data-id="element-98">Email</h4>
                  <p className="text-gray-400" data-id="element-99">info@bluerainproductions.nl</p>
                </div>
              </div>
            </div>
          </div>
          <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-100">
            {formStatus === 'success' ? <div className="bg-blue-900/20 border border-blue-600/30 rounded-sm p-6 text-center" data-id="element-101">
                <CheckCircleIcon className="w-12 h-12 text-blue-400 mx-auto mb-4" data-id="element-102" />
                <h3 className="text-xl font-semibold mb-2" data-id="element-103">
                  Bericht verzonden!
                </h3>
                <p className="text-gray-300" data-id="element-104">
                  Bedankt voor uw bericht. We nemen zo snel mogelijk contact met
                  u op.
                </p>
              </div> : <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-id="element-105">
                <div data-id="element-106">
                  <label htmlFor="name" className="block mb-2 text-sm font-medium" data-id="element-107">
                    Naam
                  </label>
                  <input type="text" id="name" name="name" value={formState.name} onChange={handleChange} className={`w-full p-3 bg-gray-900 border ${formErrors.name ? 'border-red-500' : 'border-gray-800'} rounded-sm text-white focus:outline-none focus:border-blue-400 transition-colors`} placeholder="Uw naam" data-id="element-108" />
                  {formErrors.name && <p className="mt-1 text-sm text-red-500" data-id="element-109">
                      {formErrors.name}
                    </p>}
                </div>
                <div data-id="element-110">
                  <label htmlFor="email" className="block mb-2 text-sm font-medium" data-id="element-111">
                    Email
                  </label>
                  <input type="email" id="email" name="email" value={formState.email} onChange={handleChange} className={`w-full p-3 bg-gray-900 border ${formErrors.email ? 'border-red-500' : 'border-gray-800'} rounded-sm text-white focus:outline-none focus:border-blue-400 transition-colors`} placeholder="Uw emailadres" data-id="element-112" />
                  {formErrors.email && <p className="mt-1 text-sm text-red-500" data-id="element-113">
                      {formErrors.email}
                    </p>}
                </div>
                <div data-id="element-114">
                  <label htmlFor="message" className="block mb-2 text-sm font-medium" data-id="element-115">
                    Bericht
                  </label>
                  <textarea id="message" name="message" value={formState.message} onChange={handleChange} rows={5} className={`w-full p-3 bg-gray-900 border ${formErrors.message ? 'border-red-500' : 'border-gray-800'} rounded-sm text-white focus:outline-none focus:border-blue-400 transition-colors`} placeholder="Uw bericht" data-id="element-116"></textarea>
                  {formErrors.message && <p className="mt-1 text-sm text-red-500" data-id="element-117">
                      {formErrors.message}
                    </p>}
                </div>
                <div className="hidden" aria-hidden="true" data-id="element-118">
                  <label htmlFor="honeypot" data-id="element-119">Leave this field empty</label>
                  <input type="text" id="honeypot" name="honeypot" value={formState.honeypot} onChange={handleChange} tabIndex={-1} autoComplete="off" data-id="element-120" />
                </div>
                <div className="mt-4" data-id="element-121">
                  <div ref={recaptchaRef} className="g-recaptcha" data-id="element-122"></div>
                  {formErrors.recaptcha && <p className="mt-1 text-sm text-red-500" data-id="element-123">
                      {formErrors.recaptcha}
                    </p>}
                </div>
                {formStatus === 'error' && <div className="p-3 bg-red-900/20 border border-red-800 rounded-sm flex items-center" data-id="element-124">
                    <AlertCircleIcon className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" data-id="element-125" />
                    <p className="text-sm text-red-200" data-id="element-126">
                      {errorMessage || 'Er is iets misgegaan bij het verzenden. Probeer het later opnieuw.'}
                    </p>
                  </div>}
                <button type="submit" disabled={formStatus === 'loading'} className={`w-full ${formStatus === 'loading' ? 'bg-blue-700' : 'bg-blue-400 hover:bg-blue-500'} text-black font-medium py-3 px-6 rounded-sm transition-colors flex items-center justify-center`} data-id="element-127">
                  {formStatus === 'loading' ? <>
                      <LoaderIcon className="w-5 h-5 mr-2 animate-spin" data-id="element-128" />
                      Verzenden...
                    </> : 'Verstuur bericht'}
                </button>
              </form>}
          </div>
        </div>
      </div>
    </section>;
};